// PauseLanding.tsx — the gentle landing after a marked pause point ('pause') and
// the forced exit pause that closes a skill chapter ('skillExit'). Always
// overrideable, never a lock. No streak/skip language; pacing stays invisible.
import { Btn, Display, Body } from '@/components/ui';
import { Icon, type IconName } from '@/components/icons';
import type { Handoff } from '@/data/pacing.generated';

function HandoffCard({ handoff, onHandoff }: { handoff: Handoff; onHandoff: (h: Handoff) => void }) {
  const map: Record<Handoff, { icon: IconName; title: string; sub: string; action?: string }> = {
    meditation: { icon: 'headphones', title: 'A meditation might help', sub: 'Settle with one in My Stuff', action: 'Open meditations' },
    calm: { icon: 'leaf', title: 'Calm your body first', sub: 'A short breathing practice', action: 'Calm your body' },
    journal: { icon: 'book', title: 'Take this to your journal', sub: 'Write it through in your own hand' },
  };
  const m = map[handoff];
  return (
    <div style={{ marginTop: 20, padding: '15px 16px', borderRadius: 16, border: '1px solid rgba(29,158,117,0.18)', background: 'rgba(29,158,117,0.05)' }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        <span style={{ color: 'var(--green-deep)', flexShrink: 0 }}>
          <Icon name={m.icon} size={20} />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: 'Fraunces, Georgia, serif', fontSize: 15.5, color: 'var(--ink)' }}>{m.title}</div>
          <Body dim size={12.5}>{m.sub}</Body>
        </div>
      </div>
      {m.action && (
        <Btn full variant="soft" size="sm" onClick={() => onHandoff(handoff)} style={{ marginTop: 11 }}>
          {m.action}
        </Btn>
      )}
    </div>
  );
}

export function PauseLanding({
  variant,
  handoff,
  onDoneForNow,
  onContinue,
  onHandoff,
}: {
  variant: 'pause' | 'skillExit';
  handoff?: Handoff;
  /** Primary for 'pause' ("I'm done for now"); the "done" action for 'skillExit'. */
  onDoneForNow: () => void;
  /** Secondary for 'pause' ("Continue"). Unused for 'skillExit'. */
  onContinue: () => void;
  onHandoff: (h: Handoff) => void;
}) {
  const isPause = variant === 'pause';
  // Premium centred treatment matching ModuleCelebration: a medallion under a soft
  // glow, centred copy, and a sticky CTA tray — not a top-left stack.
  const accent = isPause ? 'var(--green-deep)' : 'var(--amber-deep)';
  const glow = isPause
    ? 'radial-gradient(120% 90% at 50% -12%, rgba(150,210,184,0.5), rgba(29,158,117,0.12) 46%, transparent 70%)'
    : 'radial-gradient(120% 90% at 50% -12%, rgba(246,212,154,0.55), rgba(232,176,84,0.16) 45%, transparent 70%)';
  const medallion = isPause
    ? 'linear-gradient(150deg, #3FB58B, #14795A)'
    : 'linear-gradient(150deg, #E1A943, #9B6712)';

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 86,
        background: 'var(--cream)',
        display: 'flex',
        flexDirection: 'column',
        animation: 'rise .42s cubic-bezier(.2,.7,.3,1)',
      }}
    >
      {/* soft light from above */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 340,
          background: glow,
          pointerEvents: 'none',
        }}
      />

      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          padding: 'calc(var(--safe-top) + 36px) 26px 24px',
          position: 'relative',
        }}
      >
        {/* medallion */}
        <div
          style={{
            width: 84,
            height: 84,
            borderRadius: '50%',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#FBF1DE',
            background: medallion,
            boxShadow: isPause
              ? '0 1px 0 rgba(180,230,208,0.4) inset, 0 22px 44px -16px rgba(20,121,90,0.55)'
              : '0 1px 0 rgba(246,212,154,0.4) inset, 0 22px 44px -16px rgba(150,96,22,0.6)',
            marginBottom: 22,
          }}
        >
          <Icon name={isPause ? 'leaf' : 'shield'} size={38} sw={2} />
        </div>

        <div
          style={{
            fontFamily: 'Inter, system-ui, sans-serif',
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: '0.16em',
            textTransform: 'uppercase',
            color: accent,
            marginBottom: 12,
          }}
        >
          {isPause ? 'A natural pause' : 'Your new skill'}
        </div>

        {isPause ? (
          <>
            <Display parts={{ pre: 'Worth pausing ', em: 'here', post: '.' }} size={27} lh={1.18} />
            <Body size={14.5} style={{ marginTop: 15, maxWidth: 340, lineHeight: 1.62 }}>
              You’ve done a real piece of work. There’s no need to push on — this kind of material settles
              better with a little space around it. Come back whenever you’re ready; your place is kept.
            </Body>
            {handoff && (
              <div style={{ width: '100%', maxWidth: 360 }}>
                <HandoffCard handoff={handoff} onHandoff={onHandoff} />
              </div>
            )}
          </>
        ) : (
          <>
            <Display parts={{ pre: 'Have this ', em: 'ready', post: '.' }} size={27} lh={1.18} />
            <Body size={14.5} style={{ marginTop: 15, maxWidth: 340, lineHeight: 1.62 }}>
              A skill only helps if it’s ready <em>before</em> you need it — not something you reach for from
              scratch in a hard moment. So take a breath and think ahead: when might this help you?
            </Body>
            <Body size={14.5} style={{ marginTop: 13, maxWidth: 340, lineHeight: 1.62 }}>
              Picture the moments you know can be difficult — a conversation with your ex, an anniversary, a
              court date, the times of day things tend to get heavy — and plan, even loosely, how you’ll use
              this then.
            </Body>
            <Body size={14.5} style={{ marginTop: 13, maxWidth: 340, lineHeight: 1.62 }}>
              And come back any time you need to — to prepare <em>before</em> something hard, or to settle
              <em> after</em> it.
            </Body>
          </>
        )}
      </div>

      {/* sticky CTA tray */}
      <div
        style={{
          position: 'relative',
          padding: '16px 26px calc(26px + var(--safe-bottom))',
          background: 'linear-gradient(180deg, transparent, var(--cream) 30%)',
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
        }}
      >
        {isPause ? (
          <>
            <Btn full size="lg" onClick={onDoneForNow} leading={<Icon name="leaf" size={18} />}>
              I’m done for today
            </Btn>
            <Btn full variant="ghost" onClick={onContinue}>
              Keep going
            </Btn>
          </>
        ) : (
          <Btn full size="lg" onClick={onDoneForNow} leading={<Icon name="check" size={18} sw={2} />}>
            I’ll be ready
          </Btn>
        )}
      </div>
    </div>
  );
}
